import React, { useContext } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import * as qs from 'query-string';
import { useLocation } from 'react-router-dom';
import { NotificationContext } from '../../../components/GrowlNotifications';
import PageNotFound from '../../../components/PageNotFound';
import { Loading } from '../../../components/Loading';
import { ACCEPT_INVITATION, INVITATION_QUERY } from '../services/account-invitation.service';
import AccountInvitationForm from '../components/AccountInvitationForm';
import { AccountType } from '../../../shared/Constants';
import EssentialAccountInvitation from '../components/EssentialAccountInvitation';

const AccountInvitationContainer = ({ children, variables }) => {
  const { loading, data, error } = useQuery(INVITATION_QUERY, {
    variables,
  });

  const [acceptInvitationMutation, acceptInvitationResult] = useMutation(ACCEPT_INVITATION);

  return children({
    invitation: { loading, data, error },
    acceptInvitation: { mutation: acceptInvitationMutation, result: acceptInvitationResult },
  });
};

/**
 * Display the account invitation page. This page requires authentication.
 * If the user is not authenticated, display a dialog for login / user registration.
 */
const AccountInvitation = () => {
  const growl = useContext(NotificationContext);
  const location = useLocation();
  const { token, userExists } = qs.parse(location.search);

  const userExistsBooleanValue = userExists === 'true';

  if (!token) {
    return <PageNotFound />;
  }

  const variables = {
    token,
  };

  return (
    <AccountInvitationContainer variables={variables}>
      {({ invitation: { loading, data, error }, acceptInvitation }) => {
        if (error) {
          growl.show({
            severity: 'error',
            sticky: true,
            detail: error.message,
          });
        }

        if (loading) {
          return <Loading />;
        }

        const { invitation, countries, locales, timeZones, availableTaxIds } = data;

        if (!data || !invitation) {
          return <PageNotFound />;
        }

        if (invitation.type === AccountType.ESSENTIAL) {
          return (
            <EssentialAccountInvitation
              invitation={invitation}
              countries={countries}
              locales={locales}
              timeZones={timeZones}
              taxIds={availableTaxIds}
              token={token}
              userExists={userExistsBooleanValue}
            />
          );
        }

        return (
          <AccountInvitationForm
            invitation={invitation}
            countries={countries}
            locales={locales}
            timeZones={timeZones}
            taxIds={availableTaxIds}
            token={token}
            userExists={userExistsBooleanValue}
            acceptInvitation={acceptInvitation}
          />
        );
      }}
    </AccountInvitationContainer>
  );
};

export default AccountInvitation;
